import { useEffect, useRef, useState } from "react";

// prime react component
import AppBreadcrumb from "../../components/CustomeBreadcrumb/AppBreadcrumb";
import { Toolbar } from "primereact/toolbar";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Toast } from "primereact/toast";
import { Avatar } from "primereact/avatar";

// data table option
import {
  convertDateFrom,
  defaultPageRowOptions,
  defaultRowOptions,
  farmerStatusOption,
  paginatorLinks,
  showingEntries,
  SkeletonbodyTemplate,
  Skeletonitems,
  getUTCStartOfDay,
  getUTCEndOfDay
} from "../../appconfig/Settings";

// farmer table list column
import { FarmerListColumns } from "../../appconfig/DataTableColumns";

// api services
import { getAllDetails, postAllData } from "../../service/ProductService";

// api constant
import { ADD_WALLET_POINT, CHANGE_SOIL_SENSE_STATUS, GET_FARMER_LIST } from "../../config/ApiConstant";
import { NavLink } from "react-router-dom";
import { Calendar } from "primereact/calendar";
import { FilterMatchMode } from "primereact/api";
import { globalNameFilterTemplate } from "../../appconfig/AppHelper";
import { globalFieldTemplate, createdAtTemplate } from "../../appconfig/TemplateSetting";
import { Dialog } from "primereact/dialog";
import { ErrorMessage, Form, Formik } from "formik";
import { walletSchema } from "../../config/Validate";
import { Dropdown } from "primereact/dropdown";
import { Badge } from "primereact/badge";
import { InputSwitch } from "primereact/inputswitch";

const FarmerList = () => {
  document.title = "Farmer | Agriown";

  // Set Toast
  const toast = useRef<Toast>(null);

  //  ref hook
  const formikRef = useRef<any>(null);

  // page load | filter properties
  const [pageLoad, setPageLoad] = useState<boolean>(false);
  const [globalFilter, setGlobalFilter] = useState<any>(null);
  const [localSwitchState, setLocalSwitchState] = useState<Record<number, boolean>>({});

  // datatable filter value sets
  const emptyFilters = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    email: { value: null, matchMode: FilterMatchMode.CONTAINS },
    mobile: { value: null, matchMode: FilterMatchMode.CONTAINS },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
  };
  const [filters, setFilters] = useState<any>(emptyFilters);

  // state to store farmer list
  const [farmerList, setFarmerList] = useState<any>([]);
  const [filteredFarmers, setFilteredFarmers] = useState<any[] | null>(null);

  // Clear stale filtered data when source data changes
  useEffect(() => {
    setFilteredFarmers(null);
  }, [farmerList]);

  const [status, setStatus] = useState<boolean>(false);


  // points and modal
  const [visible, setVisible] = useState<boolean>(false);
  const [selectedFarmer, setSelectedFarmer] = useState<any>(null);

  // Date Object
  const [dates, setDates] = useState<string | Date | Date[] | any | null>(null);

  // useefect to get farmer list on page load
  useEffect(() => {
    fetchFarmers(status, dates);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [dates, status]);


  // handle csv export
  const exportCSV = () => {
    const dataToExport = filteredFarmers !== null ? filteredFarmers : farmerList;

    if (!dataToExport || dataToExport.length === 0) {
      toast.current?.show({
        severity: "warn",
        summary: "No Data",
        detail: "No farmers available to export",
      });
      return;
    }

    // Exact CSV headers
    const headers = [
      "user_phone",
      "user_name",
      "village",
      "taluka",
      "district",
      "state",
      "country",
      "pincode",
      "email",
      "country_code",
      "password",
    ];

    // Map farmerList to CSV rows
    const rows = dataToExport.map((farmer: any) => [
      farmer.mobile || " ",
      farmer.name || " ",
      farmer.village || " ",
      farmer.taluka || " ",
      farmer.district || " ",
      farmer.state || "Gujarat",
      farmer.country || "India",
      farmer.pincode || " ",
      farmer.email || " ",
      farmer.country_code || "+91",
      farmer.password || "12345678",
    ]);

    // Create CSV content
    const csvContent =
      [headers, ...rows]
        .map((row) => {
          return row.map((value: any) => {
            return `"${value}"`;
          }).join(",");
        })
        .join("\n");


    // Download CSV
    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "not_added_soilsense_farmers.csv";
    link.click();

    URL.revokeObjectURL(url);
  };


  // left toolbar
  const leftToolbarTemplate = () => {
    return (
      <span className="mr-2 md:mr-3 search-bar">
        <i className="pi pi-search"></i>
        <InputText
          type="search"
          className="search-input"
          value={globalFilter}
          onInput={(e) => setGlobalFilter(e.currentTarget.value)}
          placeholder="Search..."
        />
      </span>
    );
  };

  //Clear Filters
  const rightToolbarTemplate = () => {
    return (
      <>
        {status === false && (
          <Button
            icon="pi pi-file-excel"
            label="Export CSV"
            className="p-button-success mr-3"
            onClick={exportCSV}
          />
        )}
        <div className="flex gap-2 align-items-center">
          <span className="text-sm font-medium text-700">
            Added in SoilSense
          </span>

          <Dropdown
            className="mr-3"
            value={status}
            options={farmerStatusOption}
            optionLabel="label"
            optionValue="value"
            placeholder="Select Status"
            onChange={(e) => setStatus(e.value)}
          />
        </div>


        <Calendar
          value={dates}
          className="mr-3 order-cle"
          dateFormat="dd/mm/yy"
          onChange={(e) => setDates(e.value)}
          selectionMode="range"
          showIcon
          readOnlyInput
        />
        <Button
          icon="pi pi-filter-slash"
          label="Clear"
          onClick={() => clearAll()}
          className="md:mr-2 p-button-outlined p-button-primary"
        ></Button>
      </>
    );
  };

  // fetch farmers
  const fetchFarmers = async (currentStatus: boolean, currentDates: any) => {
    try {
      let queryUrl = GET_FARMER_LIST + `?soilsense_status=${currentStatus}`;
      const params = new URLSearchParams();

      if (currentDates && currentDates[0] && currentDates[1]) {
        const start_date = getUTCStartOfDay(currentDates[0]);
        const end_date = getUTCEndOfDay(currentDates[1]);
        params.append("start_date", start_date);
        params.append("end_date", end_date);
      } else {
        const start_date = "2000-01-01 00:00:00";
        const today = new Date();
        const end_date = getUTCEndOfDay(today);
        params.append("start_date", start_date);
        params.append("end_date", end_date);
      }

      const queryString = params.toString();
      if (queryString) {
        queryUrl += `&${queryString}`;
      }

      const response = await getAllDetails(queryUrl);

      if (response) {
        setFarmerList(response);
        setPageLoad(true);
      } else {
        setFarmerList([]);
        setPageLoad(false);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
    }
  };

  // handle switch change
  const handleSwitchChange = async (farmerId: number, value: boolean) => {
    try {
      await postAllData(
        `${CHANGE_SOIL_SENSE_STATUS}/${farmerId}`,
        { is_soilsense_added: value }
      );

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Soil Sense status updated",
      });

      setTimeout(() => {
        fetchFarmers(status, dates);
        setLocalSwitchState({});
      }, 500);

    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
    }
  };

  //Clear All Filter
  const clearAll = () => {
    setFilters({ ...emptyFilters });
    setGlobalFilter("");
    setStatus(false);
    setDates(null);
  };

  //name Template
  const nameTemplate = (rowData: any) => {
    return (
      <>
        <NavLink to={`/farmer/detail/${btoa(rowData?.id)}`}>
          <div className="flex avatar-div align-items-center">
            {!window.cn(rowData?.profile_img) ? (
              <Avatar image={rowData?.profile_img} shape="circle" />
            ) : (
              <div className="img-fluid d-flex align-items-center">
                <Avatar
                  label={rowData?.name?.charAt(0).toUpperCase()}
                  style={{
                    backgroundColor: "#438440",
                    color: "#ffffff",
                  }}
                  shape="circle"
                />
              </div>
            )}
            <div className="ml-2 emp-name link-name">{rowData.name}</div>
          </div>
        </NavLink>
      </>
    );
  };

  // add wallet point template
  const addPointTemplate = (rowData: any) => {
    return (
      <Button
        tooltip="Add wallet point"
        tooltipOptions={{ position: "top" }}
        icon="pi pi-plus"
        onClick={() => {
          setVisible(true);
          setSelectedFarmer(rowData);
        }}
      />
    );
  };

  // add wallet point template
  const statusTemplate = (rowData: any) => {
    const getSeverity = () => {
      if (rowData?.is_soilsense_added === true) return "success";
      if (rowData?.is_soilsense_added === false) return "danger";
      return undefined;
    };

    return (
      <div className="flex flex-column align-items-center">
        <InputSwitch
          checked={
            localSwitchState[rowData.id] ?? rowData.is_soilsense_added
          }
          onChange={(e) => {
            setLocalSwitchState((prev) => ({
              ...prev,
              [rowData.id]: e.value,
            }));
            handleSwitchChange(rowData.id, e.value);
          }}
        />
        <Badge
          className="mt-2"
          severity={getSeverity()}
          value={rowData?.is_soilsense_added ? "Added" : "Not Added"}
        />
      </div>
    );
  };

  // handle close modal
  const handleCloseModal = () => {
    setVisible(false);
    setSelectedFarmer(null);
  };

  // submit points api
  const handlePointSubmit = async (
    values: any,
    { setSubmitting, resetForm }: any
  ) => {
    try {
      const payload = {
        id: selectedFarmer.id,
        wallet_points: Number(values.wallet_points),
      };

      const response = await postAllData(ADD_WALLET_POINT, payload);

      if (response) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: response.message,
        });
        resetForm();
        handleCloseModal();
        // getFarmerList(dates[0], dates[1]);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Breadcrumb */}
      <AppBreadcrumb
        currentMenu="Farmer"
        pageName="Farmer List"
        pageTitle="Farmer List"
      />
      <Toast ref={toast} />

      <div className="grid crud-demo">
        <div className="col-12">
          <div className="card">
            <Toolbar
              left={leftToolbarTemplate}
              right={rightToolbarTemplate}
            ></Toolbar>

            {pageLoad ? (
              <DataTable
                value={farmerList}
                showGridlines
                stripedRows
                globalFilter={globalFilter || null}
                rows={defaultRowOptions}
                paginator={farmerList.length >= 10}
                rowsPerPageOptions={defaultPageRowOptions}
                className="datatable-responsive p-datatable-sm"
                paginatorTemplate={paginatorLinks}
                currentPageReportTemplate={showingEntries}
                filters={{ ...filters, global: { ...filters.global, value: globalFilter || null } }}
                onFilter={(e: any) => setFilters(e.filters)}
                onValueChange={(e: any[]) => setFilteredFarmers(e)}
                emptyMessage="No Data Found"
                removableSort
              >
                {FarmerListColumns.map((col, i) => {
                  if (col.field === "name") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                        body={nameTemplate}
                        sortable
                        filter
                        filterElement={globalNameFilterTemplate}
                      />
                    );
                  } else if (col.field === "email") {
                    return (
                      <Column
                        key={col.field}
                        header={col.header}
                        body={globalFieldTemplate}
                        field={col.field}
                      />
                    );
                  } else if (col.field === "mobile") {
                    return (
                      <Column
                        key={col.field}
                        header={col.header}
                        body={globalFieldTemplate}
                        field={col.field}
                      />
                    );
                  } else if (col.field === "createdAt") {
                    return (
                      <Column
                        key={col.field}
                        header={col.header}
                        body={createdAtTemplate}
                        field={col.field}
                      />
                    );
                  } else if (col.field === "add_points") {
                    return (
                      <Column
                        key={col.field}
                        header={col.header}
                        body={addPointTemplate}
                        field={col.field}
                      />
                    );
                  } else if (col.field === "is_soilsense_added") {
                    return (
                      <Column
                        key={col.field}
                        header={col.header}
                        body={statusTemplate}
                        field={col.field}
                      />
                    );
                  } else {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                      />
                    );
                  }
                })}
              </DataTable>
            ) : (
              <DataTable value={Skeletonitems}>
                {FarmerListColumns.map((col) => (
                  <Column
                    key={col.field}
                    field={col.field}
                    header={col.header}
                    body={SkeletonbodyTemplate}
                  />
                ))}
              </DataTable>
            )}
          </div>
        </div>
      </div>

      {/* add points dialog */}
      <Dialog
        header="Add Wallet Points"
        visible={visible}
        className="mx-3 lg:mx-0"
        modal
        style={{ width: "30vw" }}
        onHide={handleCloseModal}
        draggable={false}
        footer={
          <>
            <div>
              <Button
                label="Cancel"
                icon="pi pi-times"
                onClick={handleCloseModal}
                className="p-button-secondary"
              />
              <Button
                label={formikRef.current?.isSubmitting ? "Saving..." : "Save"}
                icon="pi pi-check"
                type="button"
                loading={formikRef.current?.isSubmitting}
                disabled={formikRef.current?.isSubmitting}
                onClick={() => formikRef.current?.handleSubmit()}
                autoFocus
              />
            </div>
          </>
        }
      >
        {selectedFarmer && (
          <Formik
            initialValues={{
              wallet_points: "",
            }}
            validationSchema={walletSchema}
            innerRef={formikRef}
            onSubmit={handlePointSubmit}
          >
            {({ values, handleChange, touched, errors, handleBlur }) => (
              <Form className="p-fluid">
                <div className="field mb-3">
                  <label htmlFor="wallet_points">Wallet Points</label>
                  <InputText
                    id="wallet_points"
                    name="wallet_points"
                    value={values.wallet_points}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    keyfilter={"pint"}
                    className={
                      touched.wallet_points && errors.wallet_points
                        ? "p-invalid"
                        : ""
                    }
                  />
                  <small className="p-error">
                    <ErrorMessage name="wallet_points" />
                  </small>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </Dialog>
    </>
  );
};

export default FarmerList;
